import { __awaiter } from '../node_modules/tslib/tslib.es6.js';
import { jsxs, jsx } from 'react/jsx-runtime';
import { useState, useEffect } from 'react';
import '../analytics/events.js';
import 'uuid';
import 'lodash/includes';
import { getAppConsistentParams } from '../utils/appUtils.js';
import { getEntityImage } from '../utils/assetUtils.js';
import '../utils/brandclubStoreConfigUtils.js';
import 'numeral';
import { decodeHTMLCharacters, formatMoney } from '../utils/stringUtils.js';
import '../utils/broadcast.js';
import '../utils/constants/Constants.js';
import 'awesome-phonenumber';
import 'lodash/isEqual';
import 'lodash/get';
import 'lodash/orderBy';
import '@splitsoftware/splitio-react';
import '@tanstack/react-query';
import '../Auth/index.js';
import { CampaignType } from '../types/Campaign.js';
import { getPurchaseRewardAmountForProduct, getProductUrl, isServerSideCheckoutEligible } from '../utils/productUtils.js';
import '../api/graphQl/brand.js';
import '../api/graphQl/product.js';
import '../api/cache/appConfigQueryOptions.js';
import '@brandclub/types';
import 'lodash/omitBy';
import 'lodash/isNil';
import '../utils/logger.js';
import { FallbackImage } from '../Components/FallbackImage.js';
import { ConciergeRetailerIconGroup } from './components/ConciergeRetailerIconGroup.js';
import { ConciergeRewardPillWithText } from './components/ConciergeRewardPillWithText.js';
import { Button } from '../Components/ui/button.js';
import { cn } from '../lib/utils.js';
import _uniq from 'lodash/uniq';
import { CheckCircleIcon, ShoppingBag } from 'lucide-react';
import { customColor, themeColor } from '@brandclub/bc-widgets';

// Default card styles (used when not wrapped by ProductCardCarouselWidget)
const defaultCardStyles = {
    cardBorderColor: customColor('#dcdddc'),
    cardBorderRadius: 12,
    cardBgColor: themeColor('transparent'),
    cardBgColorHover: customColor('#F6F8FA'),
    cardPadding: 16,
    cardPaddingMobile: 10,
    titleColor: themeColor('primary'),
    titleFontFamily: 'inherit',
    titleFontSize: 14,
    titleFontWeight: 500,
    titleLineClamp: 3,
    titleLineHeight: 1.5,
    titleMarginTop: 20,
    priceColor: themeColor('primary'),
    priceFontFamily: 'inherit',
    priceFontSize: 20,
    priceFontSizeMobile: 16,
    priceFontWeight: 600,
    retailerIconSize: 25,
    retailerIconSizeMobile: 15,
    imageMaxWidth: 190,
};
// Default resolveColor that handles ColorValue objects for when no widget context is available
const defaultResolveColor = (color) => {
    if (typeof color === 'string')
        return color;
    if (color && typeof color === 'object' && 'value' in color)
        return color.value;
    return '#000000';
};
const ConciergeProductCard = ({ product, onShareClick, onAddToCart, onOpenCart, isInCart: isInCartFn, showCartActions = true, cardStyles: externalCardStyles, themeFonts = {}, resolveColor = defaultResolveColor, onProductClick, }) => {
    const { affiliateRewardAmount, hasActiveInstantReward, matchedProducts, checkoutEligibility, retailerId, retailPrice, stacklineSku, title, reviewsRating, reviewsAverageRating, reviewsCount, } = product;
    const ratingValue = reviewsRating != null && reviewsRating > 0
        ? Math.round(reviewsRating * 10) / 10
        : reviewsAverageRating != null && reviewsAverageRating > 0
            ? Math.round(reviewsAverageRating * 10) / 10
            : null;
    // Merge external styles with defaults
    const styles = externalCardStyles ? Object.assign(Object.assign({}, defaultCardStyles), externalCardStyles) : defaultCardStyles;
    const img = getEntityImage(stacklineSku, 'product');
    const cleanProductTitle = decodeHTMLCharacters(title || '');
    const purchaseRewardAmount = getPurchaseRewardAmountForProduct(product);
    const rewardPercent = !!retailPrice && !!purchaseRewardAmount ? (purchaseRewardAmount * 100) / retailPrice : 0;
    const matchedProductsRetailerIds = (matchedProducts !== null && matchedProducts !== void 0 ? matchedProducts : []).map((matchedProduct) => matchedProduct.retailerId);
    const retailerIds = _uniq([retailerId, ...matchedProductsRetailerIds]);
    const urlSearchParams = getAppConsistentParams(window.location.search);
    const targetUrl = getProductUrl({ title, stacklineSku, urlSearchParams });
    // Determine mobile view based on viewport width
    const [isMobileView, setIsMobileView] = useState(() => {
        if (typeof window === 'undefined')
            return false;
        return window.innerWidth < 500;
    });
    useEffect(() => {
        const handleResize = () => {
            setIsMobileView(window.innerWidth < 500);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    // Responsive values
    const baseFontSize = isMobileView ? 11 : 14;
    const retailerLogoWidth = isMobileView ? styles.retailerIconSizeMobile : styles.retailerIconSize;
    const cardPadding = isMobileView ? styles.cardPaddingMobile : styles.cardPadding;
    const priceFontSize = isMobileView ? styles.priceFontSizeMobile : styles.priceFontSize;
    // Check if product is already in cart using provided function
    const isInCart = isInCartFn ? isInCartFn(stacklineSku) : false;
    // Determine if we should show cart actions
    const canShowCartActions = showCartActions && isServerSideCheckoutEligible(checkoutEligibility) && (onAddToCart || onOpenCart);
    const handleAddToBag = () => __awaiter(void 0, void 0, void 0, function* () {
        if (onAddToCart) {
            try {
                yield onAddToCart(product);
                onOpenCart === null || onOpenCart === void 0 ? void 0 : onOpenCart();
            }
            catch (error) {
                console.error('Error adding to bag:', error);
            }
        }
    });
    // Resolve font family - if "inherit", use inherit; otherwise look up from themeFonts
    const titleFontKey = styles.titleFontFamily;
    const titleFontData = titleFontKey && titleFontKey !== 'inherit' ? themeFonts[titleFontKey] : null;
    const titleFontFamily = titleFontData ? `"${titleFontData.name}", sans-serif` : 'inherit';
    const priceFontKey = styles.priceFontFamily;
    const priceFontData = priceFontKey && priceFontKey !== 'inherit' ? themeFonts[priceFontKey] : null;
    const priceFontFamily = priceFontData ? `"${priceFontData.name}", sans-serif` : 'inherit';
    // Generate @font-face CSS only for uploaded fonts (not Google Fonts)
    // Google Fonts are loaded via <link> tag in document.head by ProductCardCarouselWidget
    const fontFaces = [];
    if (titleFontData && titleFontData.format !== 'google' && titleFontData.format !== 'system') {
        fontFaces.push(`
      @font-face {
        font-family: "${titleFontData.name}";
        src: url("${titleFontData.url}") format("${titleFontData.format}");
        font-display: swap;
      }
    `);
    }
    if (priceFontData &&
        priceFontData.name !== (titleFontData === null || titleFontData === void 0 ? void 0 : titleFontData.name) &&
        priceFontData.format !== 'google' &&
        priceFontData.format !== 'system') {
        fontFaces.push(`
      @font-face {
        font-family: "${priceFontData.name}";
        src: url("${priceFontData.url}") format("${priceFontData.format}");
        font-display: swap;
      }
    `);
    }
    const fontFaceCss = fontFaces.join('\n');
    // Card styles
    const cardStyle = {
        border: `1px solid ${resolveColor(styles.cardBorderColor)}`,
        width: '100%',
        height: '100%',
        borderRadius: styles.cardBorderRadius,
        padding: cardPadding,
        boxSizing: 'border-box',
        transition: 'background-color 0.4s ease-in-out',
    };
    // Title styles
    // Only set fontFamily if it's a specific font, not 'inherit'
    // This allows CSS rules to apply the theme's body font when no specific font is selected
    const titleStyle = Object.assign(Object.assign({ textDecoration: 'none', color: resolveColor(styles.titleColor) }, (titleFontFamily !== 'inherit' && { fontFamily: titleFontFamily })), { fontSize: styles.titleFontSize, fontWeight: styles.titleFontWeight, marginTop: styles.titleMarginTop, width: '100%', overflow: 'hidden', letterSpacing: 0.36, WebkitLineClamp: styles.titleLineClamp, lineHeight: styles.titleLineHeight, height: `${styles.titleLineClamp * styles.titleLineHeight}em`, display: '-webkit-box', WebkitBoxOrient: 'vertical', wordBreak: 'break-word' });
    // Price container styles
    // Only set fontFamily if it's a specific font, not 'inherit'
    // This allows CSS rules to apply the theme's body font when no specific font is selected
    const priceContainerStyle = Object.assign(Object.assign({ color: resolveColor(styles.priceColor) }, (priceFontFamily !== 'inherit' && { fontFamily: priceFontFamily })), { fontSize: priceFontSize, fontWeight: styles.priceFontWeight, marginTop: 10, marginBottom: 10, display: 'flex', columnGap: 12, alignItems: 'center' });
    // Image container styles
    const imageContainerStyle = {
        width: 'calc(100% - 24px)',
        maxWidth: styles.imageMaxWidth,
        aspectRatio: '1/1',
        backgroundColor: '#fff',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        margin: '0 auto 0',
    };
    // Hover state handler
    const [isHovered, setIsHovered] = useState(false);
    return (jsxs("div", Object.assign({ style: Object.assign(Object.assign({}, cardStyle), { backgroundColor: isHovered ? resolveColor(styles.cardBgColorHover) : resolveColor(styles.cardBgColor) }), onMouseEnter: () => setIsHovered(true), onMouseLeave: () => setIsHovered(false) }, { children: [jsx("style", { children: `
        ${fontFaceCss}
        .concierge-product-card * {
          mix-blend-mode: multiply;
        }
        .concierge-product-card img[alt*="reward-icon"] {
          mix-blend-mode: normal !important;
        }
      ` }), jsxs("div", Object.assign({ className: "concierge-product-card" }, { children: [jsx(ConciergeRewardPillWithText, { onPillClick: onShareClick, rewardAmount: affiliateRewardAmount !== null && affiliateRewardAmount !== void 0 ? affiliateRewardAmount : 0, rewardCampaignType: CampaignType.AffiliateRewardCampaign, containerStyle: { justifyContent: 'end' }, baseFontSize: baseFontSize }), jsx("a", Object.assign({ href: targetUrl, target: "_blank", rel: "noreferrer", style: imageContainerStyle, onClick: onProductClick }, { children: jsx(FallbackImage, { src: img, fallbackImage: img.replace('media.brandclub.com', 'media-api.brandclub.com'), alt: title, style: {
                                maxHeight: '100%',
                                maxWidth: '100%',
                                aspectRatio: '1',
                                objectFit: 'contain',
                            } }) })), jsx("a", Object.assign({ href: targetUrl, target: "_blank", rel: "noreferrer", style: titleStyle, onClick: onProductClick }, { children: cleanProductTitle })), jsxs("div", Object.assign({ style: priceContainerStyle }, { children: [jsx("div", Object.assign({ style: { display: 'flex', alignItems: 'center' } }, { children: formatMoney(retailPrice) })), jsx(ConciergeRetailerIconGroup, { retailerIds: retailerIds, iconSize: retailerLogoWidth, maxSize: 5 })] })), ratingValue != null && (jsxs("div", Object.assign({ style: {
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4,
                            marginTop: 2,
                            fontSize: baseFontSize,
                            color: resolveColor(styles.titleColor),
                            opacity: 0.9,
                        } }, { children: [jsx("span", Object.assign({ style: { color: '#ED792F' } }, { children: "\u2605" })), jsx("span", { children: ratingValue }), reviewsCount != null && reviewsCount > 0 && (jsxs("span", Object.assign({ style: { opacity: 0.85 } }, { children: ["(", reviewsCount.toLocaleString(), " ratings)"] })))] }))), canShowCartActions && (jsx("div", Object.assign({ style: { marginTop: 8, marginBottom: 8 } }, { children: isInCart ? (jsxs(Button, Object.assign({ variant: "outline", onClick: onOpenCart, className: cn('tw:w-full tw:h-10 tw:text-sm tw:border-[#6b7280] tw:text-[#6b7280]', 'tw:normal-case tw:hover:border-[#4b5563] tw:hover:bg-[#f9fafb]', 'max-[500px]:tw:h-9 max-[500px]:tw:text-xs'), style: {
                                width: '100%',
                                height: isMobileView ? 36 : 40,
                                fontSize: isMobileView ? 12 : 14,
                                borderColor: '#6b7280',
                                color: '#6b7280',
                                textTransform: 'none',
                            } }, { children: [jsx(CheckCircleIcon, { style: { width: 16, height: 16, marginRight: 8 } }), "Added to Bag"] }))) : (jsxs(Button, Object.assign({ variant: "default", onClick: handleAddToBag, className: cn('tw:w-full tw:h-10 tw:text-sm tw:text-white tw:normal-case', 'max-[500px]:tw:h-9 max-[500px]:tw:text-xs'), style: {
                                width: '100%',
                                height: isMobileView ? 36 : 40,
                                fontSize: isMobileView ? 12 : 14,
                                backgroundColor: 'var(--color-primary, #1e293b)',
                                color: 'var(--color-primary-foreground, white)',
                                textTransform: 'none',
                            } }, { children: [jsx(ShoppingBag, { style: { width: 16, height: 16, marginRight: 8 } }), "Add to Bag"] }))) }))), jsx(ConciergeRewardPillWithText, { rewardAmount: purchaseRewardAmount !== null && purchaseRewardAmount !== void 0 ? purchaseRewardAmount : 0, rewardCampaignType: hasActiveInstantReward ? CampaignType.DailyDealsRewardCampaign : CampaignType.PurchaseRewardCampaign, rewardPercent: rewardPercent, rewardPercentText: "reward", containerStyle: isMobileView ? { columnGap: 8 } : undefined, baseFontSize: baseFontSize })] }))] })));
};

export { ConciergeProductCard };
//# sourceMappingURL=ConciergeProductCard.js.map
